/**
 * DB-2: money is formatted with Intl.NumberFormat from minor units. No
 * hand-rolled currency formatting — "$" + (n/100).toFixed(2) is wrong for JPY,
 * wrong for every locale that puts the symbol last, and wrong for negatives.
 */
const EXPONENT_OVERRIDES = {
  BIF: 0, CLP: 0, DJF: 0, GNF: 0, ISK: 0, JPY: 0, KMF: 0, KRW: 0, PYG: 0,
  RWF: 0, UGX: 0, VND: 0, VUV: 0, XAF: 0, XOF: 0, XPF: 0,
  BHD: 3, IQD: 3, JOD: 3, KWD: 3, LYD: 3, OMR: 3, TND: 3,
};

export const currencyExponent = (currency) => {
  const code = String(currency ?? 'USD').toUpperCase();
  return Object.hasOwn(EXPONENT_OVERRIDES, code) ? EXPONENT_OVERRIDES[code] : 2;
};

/**
 * The division here is for *display only*. The value is never sent back to the
 * server, so it cannot reintroduce a float into a stored amount (TS-9).
 */
export function formatMoney(minorUnits, currency = 'USD', options = {}) {
  if (minorUnits == null || Number.isNaN(Number(minorUnits))) return '—';
  const code = String(currency).toUpperCase();
  const exponent = currencyExponent(code);
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: code,
      // Without this, a US locale renders USD as "US$18.50" — correct, but not
      // what a shopper expects to see on a checkout page.
      currencyDisplay: 'narrowSymbol',
      minimumFractionDigits: exponent,
      maximumFractionDigits: exponent,
      ...options,
    }).format(Number(minorUnits) / 10 ** exponent);
  } catch {
    // An unknown currency code must still render something truthful.
    return `${(Number(minorUnits) / 10 ** exponent).toFixed(exponent)} ${code}`;
  }
}

/** Parses a typed amount into minor units, rejecting excess precision (API-2). */
export function toMinorUnits(input, currency = 'USD') {
  const raw = String(input ?? '').trim().replace(/,/g, '');
  if (!/^\d*\.?\d*$/.test(raw) || raw === '' || raw === '.') return null;
  const exponent = currencyExponent(currency);
  const [whole, fraction = ''] = raw.split('.');
  if (fraction.length > exponent) return null;
  const minor = Number(`${whole || '0'}${fraction.padEnd(exponent, '0')}`);
  return Number.isSafeInteger(minor) ? minor : null;
}

/** The inverse, for pre-filling an input from a stored amount. */
export function fromMinorUnits(minorUnits, currency = 'USD') {
  if (minorUnits == null) return '';
  const exponent = currencyExponent(currency);
  return (Number(minorUnits) / 10 ** exponent).toFixed(exponent);
}

const RELATIVE = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' });

/**
 * DB-3: relative time for recent events, absolute dates beyond a day. "3 minutes
 * ago" is what someone wants while watching a payment; "12 Mar" is what they
 * want when reading last quarter's.
 */
export function formatTimestamp(value, { now = Date.now } = {}) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';

  const diffSeconds = Math.round((date.getTime() - now()) / 1000);

  if (Math.abs(diffSeconds) < 60) return RELATIVE.format(diffSeconds, 'second');
  if (Math.abs(diffSeconds) < 3600) return RELATIVE.format(Math.round(diffSeconds / 60), 'minute');
  if (Math.abs(diffSeconds) < 86400) return RELATIVE.format(Math.round(diffSeconds / 3600), 'hour');

  return new Intl.DateTimeFormat(undefined, {
    day: 'numeric',
    month: 'short',
    year: date.getFullYear() === new Date().getFullYear() ? undefined : 'numeric',
  }).format(date);
}

export const formatExactTimestamp = (value) =>
  value
    ? new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'medium' }).format(new Date(value))
    : '—';

export const formatNumber = (value) => (value == null ? '—' : new Intl.NumberFormat().format(value));

export const formatPercent = (value) =>
  value == null ? '—' : `${new Intl.NumberFormat(undefined, { maximumFractionDigits: 1 }).format(value)}%`;

/** Turns pay_50y7SnWJhTBbmGhQjuoxwbSV into pay_50y7…wbSV for a dense table. */
export const truncateId = (id, lead = 12, tail = 4) => {
  const value = String(id ?? '');
  return value.length <= lead + tail + 1 ? value : `${value.slice(0, lead)}…${value.slice(-tail)}`;
};
